// The keycap size counts the admin screens list: how many keycaps of each physical size,
// and of each size per row, a layout (LayoutEditScreen, ProcessOrderScreen) or a mold
// (MoldEditScreen) holds, and how the sizes spread over all layouts (LayoutListScreen); and the
// total over the sizes, the layout editor's keycap count (chain 9, Oct 2026).

const getKeycapSizeLabels = (keycap) => {
  let size, sizeWithRow;

  if (keycap.isIsoEnter) {
    size = 'iso enter';
    sizeWithRow = `[${keycap.row}] iso enter`;
  } else {
    size = `${keycap.width}x${keycap.height}`;
    sizeWithRow = `[${keycap.row}] ${keycap.width}x${keycap.height}`;

    if (keycap.isConvex && keycap.isOffCentered) {
      size += ' (convex + off-centered)';
      sizeWithRow += ' (convex + off-centered)';
    } else if (keycap.isConvex) {
      size += ' (convex)';
      sizeWithRow += ' (convex)';
    } else if (keycap.stepped) {
      size += ' (stepped)';
      sizeWithRow += ' (stepped)';
    } else if (keycap.hasHomingBar) {
      size += ' (homing bar)';
      sizeWithRow += ' (homing bar)';
    } else if (keycap.width < keycap.height) {
      size = `${keycap.height}x${keycap.width}`;
    }
  }

  return { size, sizeWithRow };
}; // the size a keycap is listed under, without its row (a standing 1x2 counts as a lying 2x1 there) and with it

const compareKeycapSizes = (a, b) => {
  if (a[0] === 'iso enter') return 1;
  if (b[0] === 'iso enter') return -1;

  const [aWidth, aHeight] = a[0].split(' ')[0].split('x').map(Number);
  const [bWidth, bHeight] = b[0].split(' ')[0].split('x').map(Number);

  if (aHeight !== bHeight) {
    return aHeight - bHeight;
  }
  return aWidth - bWidth;
}; // sorting the array by height and width, ensuring 'iso enter' is at the end

export const calculateKeycapSizes = (layout) => {
  const keycapSizes = {};
  const keycapSizesWithRow = {};

  layout.forEach((row) => {
    row.forEach((keycap) => {
      if (keycap.isSpacer) {
        return; // skip spacers
      }

      const { size, sizeWithRow } = getKeycapSizeLabels(keycap);

      if (keycapSizes[size]) {
        keycapSizes[size] += 1;
      } else {
        keycapSizes[size] = 1;
      }

      if (keycapSizesWithRow[sizeWithRow]) {
        keycapSizesWithRow[sizeWithRow] += 1;
      } else {
        keycapSizesWithRow[sizeWithRow] = 1;
      }
    });
  }); // creates an object containing all different keycap sizes and the number of times they are used in the layout

  const keycapSizesArray = Object.entries(keycapSizes); // converting keycapSizes object to an array for sorting
  const keycapSizesWithRowArray = Object.entries(keycapSizesWithRow); // converting keycapSizesWithRow object to an array for sorting

  keycapSizesArray.sort(compareKeycapSizes);
  keycapSizesWithRowArray.sort(compareKeycapSizes);

  return {
    keycapSizes: keycapSizesArray,
    keycapSizesWithRow: keycapSizesWithRowArray,
  };
}; // this function calculates the number of keycaps of the same size and sorts them from smallest height to largest, and from smallest width to largest. The Iso Enter keycap will be placed at the end.

export const calculateKeycapSizesForMultipleLayouts = (layouts) => {
  // Initialize objects to store keycap size information
  const keycapSizes = {};
  const keycapSizesWithRow = {};
  const layoutUsage = {};
  const layoutUsageWithRow = {};

  // Iterate over each layout
  layouts.forEach(({ layout, layoutName, isActualLayout }) => {
    // Skip layouts that are not actual layouts
    if (!isActualLayout) return;

    const layoutKeycapSizes = {};
    const layoutKeycapSizesWithRow = {};

    // Iterate over each row in the layout
    layout.forEach((row) => {
      // Iterate over each keycap in the row
      row.forEach((keycap) => {
        // Skip spacer keycaps
        if (keycap.isSpacer) return;

        // Determine keycap size and size with row information
        const { size, sizeWithRow } = getKeycapSizeLabels(keycap);

        // Update keycap size counts without row information
        if (!layoutKeycapSizes[size]) {
          layoutKeycapSizes[size] = 0;
        }
        layoutKeycapSizes[size] += 1;

        if (keycapSizes[size]) {
          keycapSizes[size].total += 1;
          if (layoutKeycapSizes[size] > keycapSizes[size].maxFrequency) {
            keycapSizes[size].maxFrequency = layoutKeycapSizes[size];
            keycapSizes[size].maxFrequencyLayouts = [layoutName];
          } else if (
            layoutKeycapSizes[size] === keycapSizes[size].maxFrequency
          ) {
            keycapSizes[size].maxFrequencyLayouts.push(layoutName);
          }
        } else {
          keycapSizes[size] = {
            total: 1,
            layoutCount: 0,
            maxFrequency: layoutKeycapSizes[size],
            maxFrequencyLayouts: [layoutName],
            layouts: new Set(),
          };
        }
        keycapSizes[size].layouts.add(layoutName);

        // Update keycap size counts with row information, excluding 'Uniform' row
        if (keycap.row !== 'Uniform') {
          if (!layoutKeycapSizesWithRow[sizeWithRow]) {
            layoutKeycapSizesWithRow[sizeWithRow] = 0;
          }
          layoutKeycapSizesWithRow[sizeWithRow] += 1;

          if (keycapSizesWithRow[sizeWithRow]) {
            keycapSizesWithRow[sizeWithRow].total += 1;
            if (
              layoutKeycapSizesWithRow[sizeWithRow] >
              keycapSizesWithRow[sizeWithRow].maxFrequency
            ) {
              keycapSizesWithRow[sizeWithRow].maxFrequency =
                layoutKeycapSizesWithRow[sizeWithRow];
              keycapSizesWithRow[sizeWithRow].maxFrequencyLayouts = [
                layoutName,
              ];
            } else if (
              layoutKeycapSizesWithRow[sizeWithRow] ===
              keycapSizesWithRow[sizeWithRow].maxFrequency
            ) {
              keycapSizesWithRow[sizeWithRow].maxFrequencyLayouts.push(
                layoutName
              );
            }
          } else {
            keycapSizesWithRow[sizeWithRow] = {
              total: 1,
              layoutCount: 0,
              maxFrequency: layoutKeycapSizesWithRow[sizeWithRow],
              maxFrequencyLayouts: [layoutName],
              layouts: new Set(),
            };
          }
          keycapSizesWithRow[sizeWithRow].layouts.add(layoutName);
        }
      });
    });

    // Update layout count for each keycap size without row information
    Object.keys(layoutKeycapSizes).forEach((size) => {
      if (!layoutUsage[size]) {
        layoutUsage[size] = new Set();
      }
      layoutUsage[size].add(layoutName);
      keycapSizes[size].layoutCount = layoutUsage[size].size;
    });

    // Update layout count for each keycap size with row information
    Object.keys(layoutKeycapSizesWithRow).forEach((sizeWithRow) => {
      if (!layoutUsageWithRow[sizeWithRow]) {
        layoutUsageWithRow[sizeWithRow] = new Set();
      }
      layoutUsageWithRow[sizeWithRow].add(layoutName);
      keycapSizesWithRow[sizeWithRow].layoutCount =
        layoutUsageWithRow[sizeWithRow].size;
    });
  });

  // Convert keycap sizes object to array and sort them
  const keycapSizesArray = Object.entries(keycapSizes).map(([size, stats]) => [
    size,
    stats.total,
    stats.layoutCount,
    stats.maxFrequency,
    Array.from(stats.layouts), // Convert Set to Array
    stats.maxFrequencyLayouts,
  ]);

  const keycapSizesWithRowArray = Object.entries(keycapSizesWithRow).map(
    ([sizeWithRow, stats]) => [
      sizeWithRow,
      stats.total,
      stats.layoutCount,
      stats.maxFrequency,
      Array.from(stats.layouts), // Convert Set to Array
      stats.maxFrequencyLayouts,
    ]
  );

  // Sort keycap sizes by height and width, ensuring 'iso enter' is at the end
  keycapSizesArray.sort(compareKeycapSizes);
  keycapSizesWithRowArray.sort(compareKeycapSizes);

  return {
    keycapSizes: keycapSizesArray,
    keycapSizesWithRow: keycapSizesWithRowArray,
  };
}; // for the layout list's statistics: per size, how many keycaps all actual layouts hold, how many layouts use the size, the most one layout holds and which layouts those are

export const countKeycaps = (frequencies) =>
  frequencies.keycapSizes.reduce((sum, [, count]) => sum + count, 0); // the keycaps a layout holds, summed over its sizes: the layout editor's numOfKeycaps, at the load and after every edit (two copies of the sum until chain 9)
